import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { EmployeService } from './employe-service';
import { MembreEmplacement } from '../entites/membre-emplacement';

/**
 * Service that manages deferred loading of employee current emplacements.
 * Batches requests and debounces them to optimize API calls.
 */
@Injectable({ providedIn: 'root' })
export class EmplacementCourantService implements OnDestroy {
  /** Cache of employee ID to their current MembreEmplacement */
  private cache = new Map<number, MembreEmplacement | null>();

  /** Set of employee IDs pending to be fetched */
  private pendingIds = new Set<number>();

  /** Subject that triggers the batch fetch after debounce */
  private fetchTrigger$ = new Subject<void>();

  /** BehaviorSubjects for each employee ID to notify subscribers of updates */
  private emplacementSubjects = new Map<number, BehaviorSubject<MembreEmplacement | null>>();

  /** Subscription for the fetch trigger */
  private fetchSubscription: Subscription;

  /** Flag to track if a fetch is in progress */
  private isFetching = false;

  constructor(private employeService: EmployeService) {
    // Set up the debounced batch fetch
    this.fetchSubscription = this.fetchTrigger$
      .pipe(debounceTime(750))
      .subscribe(() => this.executeBatchFetch());
  }

  ngOnDestroy(): void {
    this.fetchSubscription?.unsubscribe();
    this.emplacementSubjects.forEach(subject => subject.complete());
  }

  /**
   * Gets the current emplacement for an employee.
   * If not cached, queues the request for batch fetching.
   * @param employeId The employee ID to get the emplacement for
   * @returns Observable that emits the MembreEmplacement or null if not found
   */
  public getEmplacementCourant(employeId: number): Observable<MembreEmplacement | null> {
    // Get or create the BehaviorSubject for this employee
    if (!this.emplacementSubjects.has(employeId)) {
      this.emplacementSubjects.set(employeId, new BehaviorSubject<MembreEmplacement | null>(null));
    }

    const subject = this.emplacementSubjects.get(employeId)!;

    // If already cached, emit the cached value
    if (this.cache.has(employeId)) {
      const cached = this.cache.get(employeId);
      if (subject.value !== cached) {
        subject.next(cached);
      }
    } else {
      // Queue for fetching if not already pending
      this.queueForFetch(employeId);
    }

    return subject.asObservable();
  }

  /**
   * Queues an employee ID for batch fetching
   * @param employeId The employee ID to queue
   */
  private queueForFetch(employeId: number): void {
    if (!this.pendingIds.has(employeId) && !this.cache.has(employeId)) {
      this.pendingIds.add(employeId);
      this.fetchTrigger$.next();
    }
  }

  /**
   * Executes the batch fetch for all pending employee IDs
   */
  private executeBatchFetch(): void {
    if (this.pendingIds.size === 0 || this.isFetching) {
      return;
    }

    this.isFetching = true;
    const idsToFetch = Array.from(this.pendingIds);
    this.pendingIds.clear();

    this.employeService.ObtenirEmplacementCourant(idsToFetch).subscribe({
      next: (results: MembreEmplacement[]) => {
        // Create a map of employeId to MembreEmplacement for quick lookup
        const resultMap = new Map<number, MembreEmplacement>();
        results.forEach(membre => {
          resultMap.set(membre.employeId, membre);
        });

        // Update cache and notify subscribers
        idsToFetch.forEach(id => {
          const emplacement = resultMap.get(id) || null;
          this.cache.set(id, emplacement);

          const subject = this.emplacementSubjects.get(id);
          if (subject) {
            subject.next(emplacement);
          }
        });

        this.isFetching = false;

        // If more IDs were queued during the fetch, trigger another fetch
        if (this.pendingIds.size > 0) {
          this.fetchTrigger$.next();
        }
      },
      error: (error) => {
        console.error('Error fetching emplacements courants:', error);
        // Mark all pending as null in cache to prevent re-fetching
        idsToFetch.forEach(id => {
          this.cache.set(id, null);
          const subject = this.emplacementSubjects.get(id);
          if (subject) {
            subject.next(null);
          }
        });
        this.isFetching = false;
      }
    });
  }

  /**
   * Clears the cache for a specific employee or all employees
   * @param employeId Optional employee ID to clear. If not provided, clears all cache.
   */
  public clearCache(employeId?: number): void {
    if (employeId !== undefined) {
      this.cache.delete(employeId);
    } else {
      this.cache.clear();
    }
  }
}

